// Blooming: building apps by chatting (docs/blooming.md). A blooming thread is a thread where tagging @Bloom prompts
// a build agent (Claude Managed Agents) that writes an app *bundle*: a manifest, an actions module (run in a Dynamic Worker,
// never in Bloom's own code) and a full-view module (run in the sandboxed frame, built from the standard kit).
// Every bundle that passes Bloom's checks becomes a draft version; Publish makes one live.
//
// This file holds what both halves share: the thread and version shapes, the bundle format, and the checks that need
// no sandbox (manifest and card validation, and whether a new version stores data the way the live one does).
import { cleanName } from "./cleanName.ts";
import { MAX_FILES, MAX_SCHEDULES, MEDIA_KINDS, MIN_SCHEDULE_MINUTES, RESERVED_SLUGS, cardBlockProblems, graphemes, lookProblems, mediaFields, VIEW_PILL_FORMATS, viewPillProblems, type AppManifest, type AppRecord, type Card, type CardKind, type CollectionSpec, type FieldSpec, type InputSpec } from "./apps.ts";
import { INTEGRATION_IDS } from "./integrations.ts";
import { botProblems } from "./bots.ts";
import type { Role } from "./protocol.ts";

// ---- Threads and versions ----

export type BloomStatus =
  /** Bloom asked a question and waits for an answer. */
  | "asking"
  /** The agent is working (writing, checking). `progress` says what. */
  | "building"
  /** The last turn made a draft that passed checks. */
  | "ready"
  /** The last turn ended without a draft (gave up, an error). Prompting again tries again. */
  | "failed"
  /** Over the server's monthly budget: nothing runs until it resets or an owner raises it. */
  | "paused"
  /** Nothing yet (a thread waiting for its first prompt). */
  | "idle";

/** A blooming thread, as everyone in the server sees it (ready's `blooming`, and "blooming" events). */
export interface BloomingThread {
  /** The id of the message the thread hangs off; its stream is threadStreamId(id). */
  id: string;
  /** Where that message is (a text channel). */
  channelId: string;
  /** Who started it: the app's owner once it's published. */
  ownerId: string;
  /** The app's name and icon (from the latest draft), or a working title. */
  title: string;
  icon: string;
  status: BloomStatus;
  /** While building: what's happening ("Writing the actions…"). */
  progress?: string;
  /** Only the owner and the app's editors may prompt. */
  locked: boolean;
  /** Newest draft version (0: none yet). */
  drafts: number;
  /** The version the preview shows, and the draft's manifest (its slug is draftKey(id), so its data is a copy). */
  preview: { version: number; app: AppManifest } | null;
  /** The installed app this thread builds, once published, and its live version. */
  appSlug: string | null;
  liveVersion: number | null;
  /** A bot's author id: set when a bot's thread starts ("New bot"); app threads never have one. */
  botId?: string;
  /** A bot's thread, in its owner's DM with the bot: only the DM's members see it (docs/bots.md). */
  private?: boolean;
  createdAt: number;
  updatedAt: number;
}

/** A saved build of an app: a draft until published. Code and data are versioned separately. */
export interface AppVersion {
  threadId: string;
  /** 1, 2, 3… within the thread (which is the app's build history). */
  n: number;
  /** The bundle's content id (sha-256, 32 hex): its files live in R2 under servers/<serverId>/bundles/<id>/. */
  bundle: string;
  manifest: AppManifest;
  /** What changed, in a line (from the agent). */
  summary: string;
  /** Who asked for it, and what they said. */
  authorId: string;
  prompt: string;
  at: number;
  /** When it was last made live (null: never). */
  publishedAt: number | null;
  /** The checks it passed ("6 actions tested against sample data", "view renders"). */
  checks: string[];
  /** Built outside Bloom by the author's own AI, over the connector ("Claude Code"): its name. Absent: Bloom built it. */
  via?: string;
}

/** The app key a thread's preview runs under: its data is a copy, kept apart from the live app's. */
export const draftKey = (threadId: string) => `~${threadId}`;
export const isDraftKey = (key: string) => key.startsWith("~");

// ---- Card previews: what every command posts in chat (the blooming thread's Cards view) ----

/**
 * One command's chat cards, drawn by the host's real card renderer: its form (the private card), the cards its action
 * posts (tried on the preview's data and rolled back: nothing is written), and its data card. See docs/blooming.md.
 */
export interface CommandCardPreview {
  /** The command's name, and how it's typed ("/packing add"). */
  command: string;
  label: string;
  description: string;
  /** What the command answers with (CommandSpec.card). */
  kind: CardKind;
  /** Action cards only the caller sees ("Only you can see this"). */
  private: boolean;
  action?: string;
  view?: string;
  /** Its form, as the private card someone typing the command gets (action commands; result ones when inputs are missing). */
  form?: Card;
  /** What the form turns into once sent ("Added ✓ · Tent"). */
  done?: { text: string; detail?: string };
  /** The cards the action posts, from a dry run (rolled back) with `input`. */
  cards?: Card[];
  /** The data card (view commands). */
  data?: Card;
  /** The input it was tried with (from the bundle's tests, else made up from the inputs). */
  input?: Record<string, unknown>;
  /** Records the dry run wrote (rolled back), so cards bound to a new record draw it; null: it deleted that record. */
  writes?: { collection: string; id: string; record: AppRecord | null }[];
  /** Why there's no result card (refused, needs a file, posts nothing). */
  note?: string;
  /** Tried as this role (the viewer's role can't run it). */
  as?: Role;
}

/** One version's card previews, over the data they were tried on. */
export interface CardPreviewSet {
  version: number;
  /** The app key the data belongs to: the draft's (draftKey) or, for the live version, the app's slug. */
  app: string;
  manifest: AppManifest;
  /** That data, per collection (newest first, at most CARD_PREVIEW_RECORDS each), as the cards read it. */
  records: Record<string, AppRecord[]>;
  commands: CommandCardPreview[];
}

/** The previewed draft's cards, and the live version's when it's a different one (to compare). */
export interface CardPreviews {
  draft: CardPreviewSet | null;
  live: CardPreviewSet | null;
}

export const CARD_PREVIEW_RECORDS = 200;

/**
 * What a command's cards look like without the data in them: kinds, titles, the text with names, emphasised values
 * and numbers masked, buttons, meta count, thumbnails, the form's fields and the data card's view. Equal shapes: the
 * draft didn't change those cards.
 */
export function cardShape(p: CommandCardPreview, manifest: AppManifest): string {
  const card = (c: Card | undefined) =>
    c && {
      k: c.kind,
      t: c.title ?? null,
      x: c.text ? c.text.replace(/<@[^>]+>/g, "@").replace(/\*[^*]*\*/g, "*").replace(/\d+/g, "#") : null,
      m: c.meta?.length ?? 0,
      // Blocks: how many pills and tiles, whether there's a footnote and a stripe (their values follow the data).
      ...(c.pills || c.stats || c.footnote || c.tone ? { blk: [c.pills?.length ?? 0, c.stats?.length ?? 0, !!c.footnote, !!c.tone] } : {}),
      // Newer blocks: which ones there are (and a list's style, a chart's type), only when a card has any.
      ...(c.fields || c.progress || c.chart || c.list || c.grid || c.people || c.mediaLayout ? { blk2: [c.fields?.length ?? 0, c.progress?.length ?? 0, c.chart?.type ?? "", c.list?.style ?? (c.list ? "plain" : ""), !!c.grid, !!c.people, c.mediaLayout ?? ""] } : {}),
      b: c.buttons?.map((b) => `${b.kind}:${b.label}:${b.action ?? ""}:${b.toggleField ?? ""}`) ?? [],
      media: !!c.media,
      v: c.view ? (manifest.views?.[c.view] ?? null) : null,
      f: c.form ? { s: c.form.submit, f: c.form.fields.map((f) => [f.key, f.type, f.label ?? "", !!f.required, f.options ?? [], !!f.multiline]) } : null,
    };
  return JSON.stringify({ kind: p.kind, private: p.private, form: card(p.form), cards: (p.cards ?? []).map(card), data: card(p.data), done: p.done?.text.replace(/\d+/g, "#") ?? null });
}

/** Who may start blooming in a server (an owner's setting; default everyone). */
export type BloomWho = "everyone" | "admins";

// ---- The bundle a build produces ----

/** What the build agent hands in (the submit_bundle tool). Bloom adds the platform fields itself. */
export interface Bundle {
  manifest: AppManifest;
  /** actions.js: an ES module exporting `actions`, one handler per declared action (see docs/blooming.md). */
  actions: string;
  /** view.js: an ES module whose default export is the full view, `({ bloom, React, h, kit }) => element`. */
  view: string;
  /** Sample records per collection, for checks and for a new app's preview. */
  sample?: Record<string, Record<string, unknown>[]>;
  /** Action tests run against the sample data: every action needs at least one that should succeed. */
  tests?: BundleTest[];
  /** One line: what this version changes. */
  summary?: string;
}

export interface BundleTest {
  action: string;
  input: Record<string, unknown>;
  /** Who calls it: a sample member (default the first, an owner). */
  as?: "owner" | "member";
  /** "ok" (default) or "error": the action should refuse this input. */
  expect?: "ok" | "error";
  /**
   * Answers for integration calls in this test, by "<integration>.<endpoint>": the data to return, or
   * { error: "<code>" } (e.g. "upstream") to test the error path. Default: the catalog's example.
   */
  integrations?: Record<string, unknown>;
  /**
   * Bots: the trigger the test pretends fired, as ctx.event (docs/bots.md), e.g.
   * { on: "mention", message: { text: "@Chef hi" } }. Missing parts are filled in with sample values.
   */
  event?: Record<string, unknown>;
  /** The server's emoji and stickers in this test, by name (ctx.emoji(), ctx.stickers()). Default: sample ones. */
  emoji?: string[];
  stickers?: string[];
}

export const MAX_ACTIONS_JS = 100_000;
export const MAX_VIEW_JS = 200_000;
export const MAX_SAMPLE_RECORDS = 50;
/** Submissions that fail checks in one turn before Bloom tells the agent to stop and explain. */
export const MAX_FAILED_SUBMITS = 3;

/**
 * The kit a generated view gets as `kit` (the same components Feature requests is built from, web/src/kit/). The
 * build sandbox's render check knows exactly these names.
 */
export const KIT_COMPONENTS = ["Page", "Toolbar", "Button", "Segmented", "Select", "List", "Row", "Badge", "VoteButton", "Avatar", "EmptyState", "Panel", "ActionForm", "Checkbox", "Stat", "Stats", "Footnote", "Tabs", "MediaGrid", "MediaPicker",
  "Stack", "Grid", "Card", "Divider", "Heading", "Text", "Callout", "Spinner", "Skeleton", "Progress", "Chart", "Fields", "ItemList", "Tiles", "AvatarStack", "Table", "Timeline",
  "Input", "TextArea", "SearchInput", "Switch", "Slider", "ChipGroup", "Rating", "Stepper", "Dialog", "Disclosure"] as const;
export const KIT_HELPERS = ["useToast", "timeAgo", "formatDate"] as const;

/** Standard exceptions an app can declare (marked custom on its page). */
export const STANDARD_EXCEPTIONS = ["custom-view", "custom-card", "integration"] as const;

// ---- Manifest checks ----

const SLUG = /^[a-z][a-z0-9-]{1,23}$/;
const KEY = /^[a-zA-Z][a-zA-Z0-9_]{0,31}$/;
const INPUT_TYPES = ["text", "number", "choice", "datetime", "member", "channel", "record", "media"];

/** A media input's or field's accept and maxFiles. */
function checkMedia(where: string, s: { accept?: unknown; maxFiles?: unknown }, err: (s: string) => void) {
  if (s.accept !== undefined && (!Array.isArray(s.accept) || !s.accept.length || s.accept.some((k) => !MEDIA_KINDS.includes(k)))) err(`${where} accept must list "image" and/or "video"`);
  if (s.maxFiles !== undefined && (!Number.isInteger(s.maxFiles) || (s.maxFiles as number) < 1 || (s.maxFiles as number) > MAX_FILES)) err(`${where} maxFiles must be 1–${MAX_FILES}`);
}
const FIELD_TYPES = [...INPUT_TYPES, "members"];

export interface ManifestContext {
  /** Slugs (and aliases) of other installed apps: taken. */
  taken: Map<string, string>;
  /** The slug this app already has (once published, it can't change). */
  fixedSlug?: string | null;
}

/**
 * Everything wrong with a manifest a build produced, as messages for the build agent (empty: fine). Checks shapes,
 * names, references between commands, actions, views and collections, and that the slug is free.
 */
export function validateManifest(m: unknown, ctx: ManifestContext): string[] {
  const errors: string[] = [];
  const err = (s: string) => errors.length < 40 && errors.push(s);
  if (!m || typeof m !== "object" || Array.isArray(m)) return ["manifest must be an object"];
  const a = m as AppManifest;
  if (typeof a.slug !== "string" || !SLUG.test(a.slug)) err(`slug "${String(a.slug)}" must be 2–24 lowercase letters, digits or dashes, starting with a letter`);
  else {
    if (RESERVED_SLUGS.includes(a.slug)) err(`slug "${a.slug}" is reserved (${RESERVED_SLUGS.join(", ")}); pick another`);
    const owner = ctx.taken.get(a.slug);
    if (owner) err(`slug "${a.slug}" is taken by the app "${owner}" in this server; pick another slug and tell the people in the thread about the new name`);
    if (ctx.fixedSlug && a.slug !== ctx.fixedSlug) err(`this app is published as /${ctx.fixedSlug}; keep slug "${ctx.fixedSlug}"`);
  }
  if (typeof a.name !== "string" || !a.name.trim() || a.name.length > 40) err("name must be 1–40 characters");
  else if (cleanName(a.name) !== a.name.normalize("NFKC")) {
    // Only NFKC (an ellipsis, "™", a decomposed accent) is fine: the app is stored under the cleaned name. A hidden character, a
    // control or an extra space is not, and the message says what the name would be without it.
    err(`name can't have control, direction or zero-width characters, or extra spaces (write it as plain text, one space between words): use "${cleanName(a.name)}"`);
  } else if (cleanName(a.name).length > 40) err("name must be 1–40 characters (after NFKC normalization)");
  if (typeof a.icon !== "string" || !a.icon.trim() || [...a.icon].length > 4) err("icon must be one emoji");
  if (typeof a.description !== "string" || !a.description.trim() || a.description.length > 200) err("description must be 1–200 characters");
  for (const al of a.aliases ?? []) {
    if (typeof al !== "string" || !SLUG.test(al)) err(`alias "${String(al)}" isn't a valid slug`);
    else if (RESERVED_SLUGS.includes(al) || ctx.taken.has(al)) err(`alias "${al}" is reserved or taken`);
  }
  for (const p of lookProblems(a.look)) err(p);
  if (a.exceptions !== undefined && (!Array.isArray(a.exceptions) || a.exceptions.some((x) => !(STANDARD_EXCEPTIONS as readonly string[]).includes(x)))) {
    err(`exceptions may only list ${STANDARD_EXCEPTIONS.join(", ")}`);
  }

  // Collections
  const colls = a.collections && typeof a.collections === "object" ? a.collections : null;
  if (!colls || !Object.keys(colls).length) err("declare at least one collection");
  const collNames = colls ? Object.keys(colls) : [];
  if (collNames.length > 8) err("at most 8 collections");
  for (const [cn, c] of Object.entries(colls ?? {}) as [string, CollectionSpec][]) {
    if (!KEY.test(cn)) err(`collection name "${cn}" must be a simple identifier`);
    if (!c || typeof c !== "object" || !c.fields || typeof c.fields !== "object") {
      err(`collection "${cn}" needs fields`);
      continue;
    }
    if (typeof c.description !== "string" || !c.description.trim()) err(`collection "${cn}" needs a description`);
    const fields = Object.entries(c.fields) as [string, FieldSpec][];
    if (!fields.length || fields.length > 30) err(`collection "${cn}" needs 1–30 fields`);
    for (const [fn, f] of fields) {
      if (!KEY.test(fn)) err(`field "${cn}.${fn}" must be a simple identifier`);
      if (!f || !FIELD_TYPES.includes(f.type)) err(`field "${cn}.${fn}" has unknown type "${f?.type}" (one of ${FIELD_TYPES.join(", ")})`);
      if (f?.type === "choice" && (!Array.isArray(f.options) || !f.options.length)) err(`choice field "${cn}.${fn}" needs options`);
      if (f?.type === "media") checkMedia(`media field "${cn}.${fn}"`, f, err);
    }
  }

  // Actions
  const actions = a.actions && typeof a.actions === "object" ? a.actions : null;
  if (!actions || !Object.keys(actions).length) err("declare at least one action");
  if (actions && Object.keys(actions).length > 20) err("at most 20 actions");
  for (const [an, spec] of Object.entries(actions ?? {})) {
    if (!KEY.test(an)) err(`action name "${an}" must be a simple identifier`);
    if (!spec || typeof spec.description !== "string" || !spec.description.trim()) err(`action "${an}" needs a description`);
    if (!Array.isArray(spec?.inputs)) {
      err(`action "${an}" needs an inputs list (it can be empty)`);
      continue;
    }
    if (spec.roles !== undefined && (!Array.isArray(spec.roles) || spec.roles.some((r) => !["owner", "admin", "member"].includes(r)))) err(`action "${an}" roles must be owner, admin or member`);
    checkInputs(`action "${an}"`, spec.inputs, collNames, err);
  }

  // Views
  const views = a.views && typeof a.views === "object" ? a.views : {};
  for (const [vn, v] of Object.entries(views)) {
    if (!KEY.test(vn)) err(`view name "${vn}" must be a simple identifier`);
    const c = colls && Object.hasOwn(colls, v?.collection) ? colls[v.collection] : undefined;
    if (!c) {
      err(`view "${vn}" reads collection "${v?.collection}", which isn't declared`);
      continue;
    }
    if (typeof v.title !== "string" || !v.title.trim()) err(`view "${vn}" needs a title`);
    if (!Number.isInteger(v.limit) || v.limit < 1 || v.limit > 10) err(`view "${vn}" limit must be 1–10`);
    for (const f of [v.titleField, ...(v.metaFields ?? []), ...(v.sortBy ? [v.sortBy] : []), ...(v.countField ? [v.countField] : [])]) {
      if (!Object.hasOwn(c.fields, f)) err(`view "${vn}" uses field "${f}", which ${v.collection} doesn't have`);
    }
    for (const p of viewPillProblems(vn, v)) err(p);
    if (v.valueField !== undefined) {
      if (!Object.hasOwn(c.fields, v.valueField)) err(`view "${vn}" valueField "${v.valueField}" isn't a field of ${v.collection}`);
      else if (c.fields[v.valueField]!.type !== "number") err(`view "${vn}" valueField "${v.valueField}" must be a number field`);
      if (v.countField) err(`view "${vn}" has both countField and valueField: pick one for the right-hand side`);
    }
    if (v.valueFormat !== undefined && !VIEW_PILL_FORMATS.includes(v.valueFormat)) err(`view "${vn}" valueFormat must be one of ${VIEW_PILL_FORMATS.join(", ")}`);
    if (v.rank !== undefined && typeof v.rank !== "boolean") err(`view "${vn}" rank must be true or false`);
    if (v.where !== undefined) {
      if (!v.where || typeof v.where !== "object" || Array.isArray(v.where)) err(`view "${vn}" where must be an object of field → list of values`);
      else for (const [f, vals] of Object.entries(v.where)) {
        if (!Object.hasOwn(c.fields, f)) err(`view "${vn}" filters on field "${f}", which ${v.collection} doesn't have`);
        if (!Array.isArray(vals) || !vals.length || !vals.every((x) => ["string", "number", "boolean"].includes(typeof x))) err(`view "${vn}" where.${f} must be a non-empty list of values`);
      }
    }
  }

  // Commands
  if (!Array.isArray(a.commands)) err("commands must be a list (it can be empty)");
  const names = new Set<string>();
  for (const c of Array.isArray(a.commands) ? a.commands : []) {
    if (!c || typeof c.name !== "string" || !/^[a-z][a-z0-9-]{0,23}$/.test(c.name)) {
      err(`command name "${String(c?.name)}" must be lowercase letters, digits or dashes`);
      continue;
    }
    if (names.has(c.name)) err(`command "${c.name}" is declared twice`);
    names.add(c.name);
    if (typeof c.description !== "string" || !c.description.trim()) err(`command "${c.name}" needs a description`);
    if (!["action", "data", "result"].includes(c.card)) err(`command "${c.name}" card must be action, data or result`);
    if (!!c.action === !!c.view) err(`command "${c.name}" must name exactly one action or view`);
    if (c.action && !(actions && Object.hasOwn(actions, c.action))) err(`command "${c.name}" runs action "${c.action}", which isn't declared`);
    if (c.view && !Object.hasOwn(views, c.view)) err(`command "${c.name}" posts view "${c.view}", which isn't declared`);
    if (c.view && c.card !== "data") err(`command "${c.name}" posts a view, so its card is "data"`);
    if (c.action && c.card === "data") err(`command "${c.name}" runs an action, so its card is "action" or "result"`);
    const inputs: InputSpec[] = c.inputs ?? (c.action ? actions?.[c.action]?.inputs : undefined) ?? [];
    if (c.inputs) checkInputs(`command "${c.name}"`, c.inputs, collNames, err);
    if (c.freeText && !inputs.some((i: InputSpec) => i.key === c.freeText && i.type === "text")) err(`command "${c.name}" freeText "${c.freeText}" must be one of its text inputs`);
  }
  if (a.defaultCommand !== undefined && !names.has(a.defaultCommand)) err(`defaultCommand "${a.defaultCommand}" isn't one of the commands`);

  // Bots
  if (a.bot !== undefined) for (const p of botProblems(a.bot, actions, collNames)) err(p);

  // Integrations and schedules
  if (a.integrations !== undefined) {
    if (!Array.isArray(a.integrations) || a.integrations.length > 6) err("integrations must be a list of at most 6 integration ids");
    else for (const id of a.integrations) if (typeof id !== "string" || !INTEGRATION_IDS.includes(id)) err(`integration "${String(id)}" isn't in Bloom's catalog (${INTEGRATION_IDS.join(", ")})`);
    if (Array.isArray(a.integrations) && new Set(a.integrations).size !== a.integrations.length) err("integrations lists one twice");
  }
  if (a.schedules !== undefined) {
    if (!Array.isArray(a.schedules) || a.schedules.length > MAX_SCHEDULES) err(`schedules must be a list of at most ${MAX_SCHEDULES}`);
    else for (const s of a.schedules) {
      const spec = s && typeof s.action === "string" && actions && Object.hasOwn(actions, s.action) ? actions[s.action] : undefined;
      if (!spec) err(`schedule runs action "${String(s?.action)}", which isn't declared`);
      else if (spec.inputs.some((i) => i.required && i.default === undefined)) err(`scheduled action "${s.action}" can't have required inputs (nobody fills them in)`);
      if (!s || !Number.isInteger(s.everyMinutes) || s.everyMinutes < MIN_SCHEDULE_MINUTES || s.everyMinutes > 1440) err(`schedule everyMinutes must be a whole number from ${MIN_SCHEDULE_MINUTES} to 1440`);
    }
  }
  return errors;
}

function checkInputs(where: string, inputs: InputSpec[], collections: string[], err: (s: string) => void) {
  const keys = new Set<string>();
  for (const i of inputs) {
    if (!i || typeof i.key !== "string" || !KEY.test(i.key)) {
      err(`${where} has an input without a simple key`);
      continue;
    }
    if (keys.has(i.key)) err(`${where} declares input "${i.key}" twice`);
    keys.add(i.key);
    if (!INPUT_TYPES.includes(i.type)) err(`${where} input "${i.key}" has unknown type "${i.type}" (one of ${INPUT_TYPES.join(", ")})`);
    if (i.type === "choice") {
      if (!Array.isArray(i.options) || !i.options.length) err(`${where} choice input "${i.key}" needs options`);
      else if (i.default !== undefined && !i.options.includes(String(i.default))) err(`${where} input "${i.key}" default isn't one of its options`);
    }
    if (i.type === "record" && !collections.includes(i.collection ?? "")) err(`${where} record input "${i.key}" must name a declared collection`);
    if (i.type === "media") checkMedia(`${where} media input "${i.key}"`, i, err);
  }
}

/** Everything wrong with a card an action posted (the host would silently drop it otherwise). */
export function validateCard(card: Omit<Card, "app">, app: AppManifest): string[] {
  const errors: string[] = [];
  if (!card || typeof card !== "object") return ["a card must be an object"];
  if (card.kind !== "result" && card.kind !== "data") errors.push(`actions post "result" or "data" cards, not "${String(card.kind)}"`);
  if (card.kind === "result" && !card.text && !card.title) errors.push("a result card needs text (what happened)");
  if (card.kind === "data" && !(card.view && app.views && Object.hasOwn(app.views, card.view))) errors.push(`a data card needs a declared view (got "${String(card.view)}")`);
  if (card.bind && !(card.bind.collection && Object.hasOwn(app.collections, card.bind.collection))) errors.push(`card bind names collection "${card.bind?.collection}", which isn't declared`);
  if (card.media !== undefined && !(card.bind && mediaFields(app, card.bind.collection).includes(card.media))) errors.push(`card media "${String(card.media)}" must be a media field of the bound record's collection`);
  for (const b of card.buttons ?? []) {
    if (!b || typeof b.label !== "string" || !b.label.trim()) errors.push("every card button needs a label");
    else if (b.kind === "call" && !(b.action && Object.hasOwn(app.actions, b.action))) errors.push(`button "${b.label}" calls action "${b.action}", which isn't declared`);
    else if (b.kind !== "call" && b.kind !== "open") errors.push(`button "${b.label}" kind must be "call" or "open"`);
    if (b?.toggleField && !card.bind) errors.push(`button "${b.label}" has toggleField but the card binds no record`);
    if (b?.icon !== undefined && (typeof b.icon !== "string" || graphemes(b.icon.trim()).length !== 1)) errors.push(`button "${b?.label}" icon must be one emoji`);
    if (b?.danger !== undefined && typeof b.danger !== "boolean") errors.push(`button "${b?.label}" danger must be true or false`);
  }
  if ((card.buttons?.length ?? 0) > 3) errors.push("at most 3 buttons on a card");
  if (card.people?.field !== undefined) {
    const f = card.bind && Object.hasOwn(app.collections, card.bind.collection) ? app.collections[card.bind.collection]!.fields[card.people.field] : undefined;
    if (f?.type !== "members") errors.push(`card people field "${card.people.field}" must be a members field of the bound record's collection`);
  }
  if (card.mediaLayout !== undefined && card.media === undefined) errors.push("mediaLayout needs media (a media field of the bound record)");
  errors.push(...cardBlockProblems(card));
  return errors;
}

// ---- Publishing: does a new version store data the way the live one does? ----

/**
 * Reasons a version can't go live over the current one without a migration (Phase 3): a collection or field that
 * holds data is gone, a field holding data changed type, or a field became required while records lack it.
 * `hasData(collection, field?)`: whether the live app has records (with that field set).
 */
export function incompatibleChanges(live: AppManifest, next: AppManifest, hasData: (collection: string, field?: string) => boolean, missing: (collection: string, field: string) => boolean): string[] {
  const out: string[] = [];
  for (const [cn, c] of Object.entries(live.collections)) {
    const n = Object.hasOwn(next.collections, cn) ? next.collections[cn] : undefined;
    if (!n) {
      if (hasData(cn)) out.push(`the "${cn}" collection is gone, but it has data`);
      continue;
    }
    for (const [fn, f] of Object.entries(c.fields)) {
      const nf = Object.hasOwn(n.fields, fn) ? n.fields[fn] : undefined;
      if (!nf) {
        if (hasData(cn, fn)) out.push(`the field "${cn}.${fn}" is gone, but records use it`);
      } else if (nf.type !== f.type && hasData(cn, fn)) out.push(`"${cn}.${fn}" changes from ${f.type} to ${nf.type}`);
      else if (nf.options && f.options && f.options.some((o) => !nf.options!.includes(o)) && hasData(cn, fn)) out.push(`"${cn}.${fn}" drops some of its choices`);
    }
    for (const [fn, nf] of Object.entries(n.fields)) {
      if (nf.required && !c.fields[fn]?.required && missing(cn, fn)) out.push(`"${cn}.${fn}" becomes required, but some records don't have it`);
    }
  }
  return out;
}
